import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { toast } from 'react-toastify';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import Swal from 'sweetalert2';
import { 
  getTickets, 
  createTicket, 
  updateTicket, 
  assignTicket, 
  getTicketDetails, 
  getTicketStats, 
  getITUsers,
  getTicketApprovalStats,
  getPendingApprovals,
  escalateTicket,
  getEscalatedTickets,
  getDashboardData,
  searchTickets,
  addTicketComment,
  getTicketComments,
  resolveTicket,
  closeTicket,
  processEmailImport,
  getSystems
} from '../../../models/helpdeskModel';
import TicketDetailsModal from './components/TicketDetailsModal';
import NewTicketModal from './components/NewTicketModal';
import TicketApprovalWidget from './components/TicketApprovalWidget';
import EscalatedTicketsWidget from './components/EscalatedTicketsWidget';
import EscalationModal from './components/EscalationModal';
import HelpdeskDashboard from './HelpdeskDashboard';
import ITStaffAssignmentWidget from './components/ITStaffAssignmentWidget';
import Button from '../../../components/common/Button';
import CustomDatePicker from '../../../components/common/CustomDatePicker';

// Helper function to safely format priority
  const formatPriority = (priority) => {
  if (!priority) return '';
  try {
    return String(priority).toUpperCase();
  } catch (e) {
    return '';
  }
};

// Helper function to get user-friendly filter labels
const getFilterLabel = (filterType, value) => {
  const labels = {
    status: {
      'all': 'All',
      'open': 'Open',
      'in_progress': 'Assigned',
      'escalated': 'Escalated',
      'resolved': 'Resolved',
      'closed': 'Closed'
    },
    priority: {
      'all': 'All',
      'low': 'Low',
      'medium': 'Medium',
      'high': 'High',
      'urgent': 'Urgent',
      'critical': 'Critical'
    },
    assigned: {
      'all': 'All',
      'mine': 'My Tickets',
      'assigned': 'Assigned',
      'unassigned': 'Unassigned',
      'specific': 'Specific User',
      'my_approvals': 'My Approvals'
    },
    date: {
      'all': 'All Time',
      'today': 'Today',
      'week': 'This Week',
      'month': 'This Month',
      'custom': 'Custom Range'
    }
  };
  
  return labels[filterType]?.[value] || value;
};


  // Enhanced ticket type configurations
  const ticketTypes = [
    {
      id: 'support',
      title: 'Support Request',
      description: 'General technical support and assistance',
      icon: '🛠️',
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200'
    },
     {
      id: 'change_request',
      title: 'Change Request',
      description: 'Change existing system configurations or setups',
      icon: '⚡',
      color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    }, 
    {
      id: 'enhancement',
      title: 'Enhancement Request',
      description: 'New system features or computer hardsware',
      icon: '⚡',
      color: 'bg-gradient-to-r from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200'
    },
    {
      id: 'defect',
      title: 'Bug Report',
      description: 'Report system bugs and defects',
      icon: '🐛',
      color: 'bg-gradient-to-r from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
      borderColor: 'border-pink-200'
    }
  ];


// Enhanced Debounced Input
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 800,
  placeholder = "Search...",
  className = "",
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (value !== initialValue) {
        onChange(value);
      }
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, initialValue, onChange, debounce]);

  return (
    <div className="relative">
      <input 
        {...props} 
        value={value} 
        onChange={e => setValue(e.target.value)}
        placeholder={placeholder}
        className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 placeholder-gray-400 ${className}`}
      />
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>
    </div>
  );
}

function HelpTickets() {
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  // Unified flow: remove specialized modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTicketDetails, setShowTicketDetails] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [itUsers, setItUsers] = useState([]);
  const [newTicket, setNewTicket] = useState({
    subject: '',
    details: '',
    priority: 'medium',
    category: '',
    system_id: ''
  });
  
  // Ticket statistics state
  const [ticketStats, setTicketStats] = useState({
    open: 0,
    not_assigned: 0,
    provider: 0, 
    closed: 0,
    statusCounts: {
      'Open': 0,
      'Assigned': 0,
      'Escalated': 0, 
      'Closed': 0
    }
  });
  const [ticketSystems, setTicketSystems] = useState([]);
  const [ticketDailyData, setTicketDailyData] = useState({
    dates: [],
    created: [],
    closed: []
  });
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [escalatedTickets, setEscalatedTickets] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  // Apply 'My Approvals' filter from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const filter = urlParams.get('filter');
    if (filter === 'my_approvals') {
      setAssignedFilter('my_approvals');
      setActiveFilter('my_approvals');
    }
  }, []);

  // Enhanced state management
  const { can, user, isIT, getDepartment } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    new: 0,
    assigned: 0,
    escalated: 0,
    completed: 0,
    total: 0
  });
  
  // Email import state
  const [isImporting, setIsImporting] = useState(false);

  // Closed unassigned tickets distribution state
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  const [selectedITMembers, setSelectedITMembers] = useState([]);
  const [isDistributing, setIsDistributing] = useState(false);

  // Handle URL parameters for direct ticket links
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const ticketId = urlParams.get('ticket');
    
    if (ticketId && ticketId !== selectedTicketId) {
      // Open the ticket modal automatically if ticket ID is in URL
      handleOpenTicketDetails(parseInt(ticketId));
    }
  }, [location.search]);


  // Table state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({
    start: '',
    end: ''
  });
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);

  // View mode state - Load from localStorage, default to 'table'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('helpdeskViewMode');
    return savedViewMode || 'table';
  });

  // Save view mode to localStorage whenever it changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('helpdeskViewMode', mode);
  };

  // Chat state (for integrated chat)
  const [activeView, setActiveView] = useState('tickets'); // 'tickets', 'chat' 
  const [systems, setSystems] = useState([]); 

  // Get current user ID from AuthContext (more reliable than localStorage)
  const currentUserId = useMemo(() => {
    return parseInt(user?.user_id || localStorage.getItem('user_id') || '0');
  }, [user?.user_id]);

  // Modern priority and status colors with clean design
  const priorityColors = {
    low: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
    medium: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
    high: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
    urgent: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    critical: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', dot: 'bg-purple-500' }
  };

  const statusColors = {
    new: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-400' },
    open: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', dot: 'bg-green-400' },
    assigned: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200', dot: 'bg-indigo-400' },
    pending: { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-400' },
    escalated: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    resolved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-400' },
    closed: { bg: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' }
  };

  // Handle email import
  const handleEmailImport = async () => {
    if (isImporting) return;
    
    try {
      setIsImporting(true);
      const result = await processEmailImport();
      
      // ApiController returns { status, message, data }
      // Handle cases where result might be wrapped or mixed with debug output
      const isOk = result && (result.status === 1 || result.success === true);
      const data = result && (result.data || result);

      if (isOk) {
        const processed = data.processed ?? 0;
        const created = data.created_tickets ?? 0;
        const updated = data.updated_tickets ?? 0;
        
        // Only show success message if there's actual activity
        if (processed > 0 || created > 0 || updated > 0) {
          toast.success(
            `Email import completed. Processed: ${processed}, Created: ${created}, Updated: ${updated}`,
            { autoClose: 4000 }
          );
          // Refresh tickets to show newly imported/updated ones
          await loadTickets();
        } else {
          // Silent success - no emails to process
          console.log('Email import completed - no new emails to process');
        }
      } else {
        // Only show error if status explicitly indicates failure
        const message = (result && result.message) || 'Unknown error';
        console.error('Email import failed:', message, result);
        toast.error('Email import failed: ' + message);
      }
    } catch (error) {
      // Only show error for actual exceptions
      console.error('Email import error:', error);
      toast.error('Email import failed: ' + error.message);
    } finally {
      setIsImporting(false);
    }
  };

  // Modern table columns configuration with optimized sizing
  const columns = useMemo(() => [
    {
      id: 'ticket_number',
      accessorFn: row => row.ticket_number,
      header: 'ID',
      size: 80,
      cell: ({ row }) => {
        const ticket = row.original;
        const ticketId = ticket.ticket_number || ticket.ticket_id;
        return (
          <div className="font-mono text-xs font-medium text-gray-900">
            {ticketId}
          </div>
        );
      },
    },
    {
      id: 'title',
      accessorFn: row => {
        // Include title and requester name for global search
        const title = row.title || row.ticket_subject || '';
        const requesterName = row.requester_name || row.supplier_user_name || row.user_full_name || row.ticket_sender || '';
        return `${title} ${requesterName}`;
      },
      header: 'Subject & Details',
      size: 350,
      cell: ({ row }) => {
        const ticket = row.original;
        const subject = ticket.title || ticket.ticket_subject || 'No Subject';
        const originator = getOriginatorName(ticket);
        const timeAgoText = timeAgo(ticket);
        const duplicateCount = ticket.duplicate_count || 0;
        const linkCount = ticket.link_count || 0;
        
        return (
          <div className="space-y-1">
            <div className="font-medium text-gray-900 leading-tight text-sm flex items-center gap-2">
              <span>{subject.length > 50 ? `${subject.substring(0, 50)}...` : subject}</span>
              {duplicateCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-red-50 text-red-700 border-red-200" title={`${duplicateCount} duplicate link${duplicateCount>1?'s':''}`}>
                  Duplicate
                </span>
              )}
              {linkCount > 0 && (
                <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium border bg-gray-50 text-gray-700 border-gray-200" title={`${linkCount} related link${linkCount>1?'s':''}`}>
                  {linkCount} related
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-500">
              <span className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                </svg>
                <span className="truncate max-w-24">{originator}</span>
              </span>
              {(ticket.source === 'supplier_portal' || ticket.requester_type === 'supplier' || (ticket.supplier_id && Number(ticket.supplier_id) > 0)) && (
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full border border-blue-200" title="Supplier ticket"> 
                  S
                </span>
              )}
              <span>•</span>
              <span className="flex items-center space-x-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>{timeAgoText}</span>
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'ticket_type',
      accessorFn: row => row.ticket_type,
      header: 'Type',
      size: 120,
      cell: ({ row }) => {
        const ticket = row.original;
        const displayType = (ticket.ticket_number?.includes("SUP") && ticket.ticket_type === "enhancement") 
          ? "Change Request" 
          : ticket.ticket_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
        
        const typeColors = {
          'Support': 'bg-blue-50 text-blue-700 border-blue-200',
          'Enhancement': 'bg-purple-50 text-purple-700 border-purple-200',
          'Change Request': 'bg-orange-50 text-orange-700 border-orange-200',
          'Defect': 'bg-red-50 text-red-700 border-red-200' 
        };
        
        const colorClass = typeColors[displayType] || 'bg-gray-50 text-gray-700 border-gray-200';
        
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${colorClass}`}>
            {displayType}
          </span>
        );
      },
    },
    {
      id: 'priority',
      accessorFn: row => row.priority,
      header: 'Priority',
      size: 90,
      cell: ({ row }) => {
        const ticket = row.original;
        let priority = "Low";
        let priorityKey = "low";
        
        // Handle both new API format (string) and legacy format (numeric)
        const priorityValue = ticket.priority || ticket.ticket_priority;
        if (priorityValue === "medium" || priorityValue == 2) {
          priority = "Med";
          priorityKey = "medium";
        } else if (priorityValue === "high" || priorityValue == 3) {
          priority = "High";
          priorityKey = "high";
        } else if (priorityValue === "urgent" || priorityValue == 4) {
          priority = "Urgent";
          priorityKey = "urgent";
        }
        
        const colors = priorityColors[priorityKey];
        
        return (
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
              {priority}
            </span>
          </div>
        );
      },
    },
    {
      id: 'status',
      accessorFn: row => row.status,
      header: 'Status',
      size: 100,
      cell: ({ row }) => {
        const ticket = row.original;
        let status = "Unknown";
        let statusKey = "open";
        
        // Use getStatusText for consistent status determination
        const ticketStatus = getStatusText(ticket.status || ticket.ticket_status, ticket);
        const assignedTo = parseInt(ticket.assigned_to) || 0;
        
        if (ticketStatus === "open") {
          status = "Open";
          statusKey = "open";
        } else if (ticketStatus === "in_progress" || ticketStatus === "assigned") {
          status = "Assigned";
          statusKey = "assigned";
        } else if (ticketStatus === "escalated") {
          status = "Escalated";
          statusKey = "escalated";
        } else if (ticketStatus === "resolved") {
          status = "Resolved";
          statusKey = "resolved";
        } else if (ticketStatus === "completed") {
          status = "Completed";
          statusKey = "resolved"; // Use same styling as resolved
        } else if (ticketStatus === "closed") {
          status = "Closed";
          statusKey = "closed";
        } else if (ticket.ticket_parked == 1) {
          status = `Parked`;
          statusKey = "pending";
        } else {
          // Fallback: display the raw status value
          status = ticketStatus ? ticketStatus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : "Unknown";
          statusKey = "open";
        }
        
        const colors = statusColors[statusKey];
        
        return (
          <div className="flex items-center space-x-1">
            <div className={`w-2 h-2 rounded-full ${colors.dot}`}></div>
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}>
              {status}
            </span>
          </div>
        );
      },
    },
    {
      id: 'assigned_to',
      accessorFn: row => row.assigned_to,
      header: 'Assigned',
      size: 100,
      cell: ({ row }) => {
        const ticket = row.original;
        const assignedName = ticket.assigned_name || ticket.ticket_assigned_name;
        
        if (!assignedName || assignedName === 'Unassigned') {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Unassigned
            </span>
          );
        }
        
        // Get initials for assigned user
        const initials = assignedName.split(' ').map(n => n[0]).join('').substring(0, 2);
        
        return (
          <div className="flex items-center space-x-2">
            <div className="h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-xs font-medium text-blue-600">{initials}</span>
            </div>
            <span className="text-xs text-gray-900 truncate max-w-16">{assignedName}</span>
          </div>
        );
      },
    },
    {
      id: 'created_at',
      accessorFn: row => row.created_at || row.ticket_time,
      header: 'Created',
      size: 100,
      cell: ({ row }) => {
        const ticket = row.original;
        // Handle both new API format (ISO string) and legacy format (timestamp)
        const dateValue = ticket.created_at || ticket.ticket_time;
        let date;
        if (typeof dateValue === 'string') {
          date = new Date(dateValue);
        } else {
          date = new Date(dateValue * 1000);
        }
        
        return (
          <div className="text-xs text-gray-600">
            <div>{date.toLocaleDateString('en-CA')}</div>
            <div className="text-xs text-gray-400">{date.toLocaleTimeString('en-CA', {hour: '2-digit', minute: '2-digit', hour12: false})}</div>
          </div>
        );
      },
    }
  ], []);

  // Helper function to get originator name
  const getOriginatorName = (ticket) => {
    // Handle both new API format and legacy format
    return ticket.requester_name || ticket.supplier_user_name || ticket.user_full_name || ticket.ticket_sender || 'Unknown';
  };

  // Helper functions
  const getStatusText = (status, ticket) => {
    // Handle both new API format (strings) and legacy format (numbers)
    const assignedTo = parseInt(ticket?.assigned_to) || 0;
    
    if (typeof status === 'string') {
      // For string statuses, check if ticket is assigned and status is open
      if (status === 'open' && assignedTo > 0) {
        return 'in_progress';
      }
      return status; // New API already returns string status
    }
    
    // Legacy numeric status conversion
    const statusStr = String(status);
    const statusMap = {
      '1': 'open',
      '2': 'closed', 
      '3': 'escalated',
      '10': 'assigned',
      '0': 'open'
    };
    
    // Check if assigned for legacy format
    if (ticket && assignedTo > 0 && statusStr === '1') {
      return 'in_progress';
    }
    
    return statusMap[statusStr] || 'open';
  };

  const timeAgo = (ticket) => {
    // Handle both new API format and legacy format
    const timestamp = ticket.created_at || ticket.ticket_time;
    let time;
    
    if (typeof timestamp === 'string') {
      time = new Date(timestamp).getTime();
    } else {
      time = timestamp * 1000;
    }
    
    const now = Date.now();
    const diff = now - time;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    
    return new Date(time).toLocaleDateString();
  };

  // Handle filtering by IT staff member
  const handleFilterByStaffMember = (userId) => {
    if (!userId) {
      // Clear the filter
      setAssignedFilter('all');
      setActiveFilter('all');
      return;
    }
    
    // Apply filter for unassigned or specific user
    if (userId === 'unassigned') {
      setAssignedFilter('unassigned');
      setActiveFilter('not_assigned');
    } else {
      // For specific user, we'll handle this in the filteredData memo
      setAssignedFilter('specific');
      setActiveFilter(`user_${userId}`);
    }
  };

  // Check if ticket is awaiting current user's approval
  const isAwaitingMyApproval = (ticket) => {
    if (!user || !ticket) return false;

    // Parse ticket_approvers if it's a string
    let approvers = [];
    try {
      approvers = typeof ticket.ticket_approvers === 'string' 
        ? JSON.parse(ticket.ticket_approvers) 
        : (ticket.ticket_approvers || []);
    } catch (e) {
      return false;
    }

    if (!Array.isArray(approvers) || approvers.length === 0) return false;

    // Get user properties
    const userId = parseInt(user.user_id || user.id);
    const userHod = parseInt(user.hod || 0);
    const userItSupport = parseInt(user.it_support || 0);
    const userManager = parseInt(user.manager || 0);
    const userSupervisor = parseInt(user.supervisor || 0);
    const userDirector = parseInt(user.director || 0);
    const userDepartment = parseInt(user.department || user.department_id || 0);
    const ticketDepartment = parseInt(ticket.requester_department || 0);

    // Check approval status fields to determine next pending approver
    const approvalStatus = {
      manager: parseInt(ticket.ticket_manager || 0),
      hod: parseInt(ticket.ticket_hod || 0),
      it_hod: parseInt(ticket.ticket_it_hod || 0),
      director: parseInt(ticket.ticket_director || 0)
    };

    // Find the next pending approval level
    let nextApproverType = null;
    for (const approverType of approvers) {
      // Check if this level is NOT yet approved (status = 0)
      if (approverType === 'manager' && approvalStatus.manager === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType.startsWith('hod_')) {
        // Specific HOD approval (uses ticket_hod field)
        if (approvalStatus.hod === 0) {
          nextApproverType = approverType;
          break;
        }
      } else if (approverType === 'hod' && approvalStatus.hod === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType === 'it_hod' && approvalStatus.it_hod === 0) {
        nextApproverType = approverType;
        break;
      } else if (approverType === 'director' && approvalStatus.director === 0) {
        nextApproverType = approverType;
        break;
      }
    }

    if (!nextApproverType) return false;

    // Match approver type to current user
    let isMatch = false;
    
    if (nextApproverType.startsWith('hod_')) {
      // Specific HOD: hod_65 means only user 65 who is HOD
      const hodUserId = parseInt(nextApproverType.substring(4));
      isMatch = userId === hodUserId && userHod === 1;
    } else if (nextApproverType === 'manager') {
      // Manager or supervisor in same department
      isMatch = (userManager === 1 || userSupervisor === 1) && userDepartment === ticketDepartment;
    } else if (nextApproverType === 'hod') {
      // ANY HOD regardless of department (NO department check)
      isMatch = userHod === 1;
    } else if (nextApproverType === 'it_hod') {
      // IT HOD: user must have BOTH hod=1 AND it_support=1 (NO department check)
      isMatch = userHod === 1 && userItSupport === 1;
    } else if (nextApproverType === 'director') {
      // Director
      isMatch = userDirector === 1;
    }
 

    return isMatch;
  };

  // Backend handles all filtering - no client-side filtering needed
  const filteredData = useMemo(() => {
    if (!Array.isArray(tickets)) { 
      return [];
    }
    return tickets; // Return tickets as-is since backend filtered them
  }, [tickets]);

  // Detect closed unassigned tickets
  const closedUnassignedTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];
    
    return tickets.filter(ticket => {
      const status = getStatusText(ticket.status || ticket.ticket_status, ticket);
      const assignedTo = parseInt(ticket.assigned_to) || 0;
      return (status === 'closed' || status === 'resolved') && assignedTo === 0;
    });
  }, [tickets]);

  // Reload tickets whenever filters change
  useEffect(() => {
    // Only load if we're on the tickets view
    if (activeView === 'tickets') {
      loadTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, priorityFilter, assignedFilter, dateFilter, typeFilter, globalFilter, activeFilter, activeView, customDateRange]);

  // Calculate stats
  useEffect(() => {
    if (!Array.isArray(tickets)) { 
      return;
    }
    
    const newStats = tickets.reduce((acc, ticket) => {
      const status = getStatusText(ticket.status || ticket.ticket_status, ticket);
      const assignedTo = parseInt(ticket.assigned_to) || 0;
      
      // Map statuses to the expected stats structure
      if (status === 'open' && assignedTo === 0) {
        acc.new = (acc.new || 0) + 1;
      } else if (status === 'in_progress' || (status === 'open' && assignedTo > 0)) {
        acc.assigned = (acc.assigned || 0) + 1;
      } else if (status === 'escalated') {
        acc.escalated = (acc.escalated || 0) + 1;
      } else if (status === 'closed' || status === 'resolved') {
        acc.completed = (acc.completed || 0) + 1;
      }
      
      acc.total++;
      return acc;
    }, { 
      new: 0, 
      assigned: 0, 
      escalated: 0, 
      completed: 0, 
      total: 0 
    });
    
    setStats(newStats);
  }, [tickets]);

  // Count resolved tickets for bulk close button
  const resolvedTicketsCount = useMemo(() => {
    return tickets.filter(ticket => {
      const status = getStatusText(ticket.status || ticket.ticket_status, ticket);
      return status === 'resolved';
    }).length;
  }, [tickets]);

  // Initialize table with better pagination
  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      pagination,
      sorting,
      columnFilters,
      rowSelection,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: false,
    manualFiltering: true, // Backend handles filtering
    initialState: {
      pagination: {
        pageSize: 25, // Better default page size for usability
      },
      sorting: [
        {
          id: 'created_at', // Sort by creation date
          desc: true,
        },
      ],
    },
  });
 
  useEffect(() => {
    // Load auxiliary data on mount (systems, IT users, etc.)
    const loadAuxiliaryData = async () => {
      try {
        const [itUsersData, systemsData] = await Promise.all([
          getITUsers(),
          getSystems()
        ]);
        
        if (itUsersData?.status === 1 && itUsersData.data) {
          setItUsers(itUsersData.data);
        }
        
        // Handle systems data - API may return nested structure
        if (systemsData?.status === 1) {
          if (Array.isArray(systemsData.data)) {
            setSystems(systemsData.data);
          } else if (systemsData.data?.data?.rows && Array.isArray(systemsData.data.data.rows)) {
            setSystems(systemsData.data.data.rows);
          } else if (systemsData.systems && Array.isArray(systemsData.systems)) {
            setSystems(systemsData.systems);
          } else {
            setSystems([]);
          }
        }
      } catch (error) {
        console.error('Error loading auxiliary data:', error);
      }
    };
    
    loadAuxiliaryData();
    
    // Set default view to tickets
    setActiveView('tickets');
  }, []);

  // Enforce tickets view for non-IT users
  useEffect(() => {
    if (!isIT() && activeView !== 'tickets') {
      setActiveView('tickets');
    }
  }, [isIT, activeView]);

  const loadTickets = async () => {
    try {
      setIsLoading(true);
      
      // Prepare filters - send all filters to backend
      const filters = {};
      
      if (!isIT()) {
        // Filter by user's department for non-IT users
        filters.department = getDepartment();
      }
      
      // Add all active filters
      if (statusFilter !== 'all') filters.status = statusFilter;
      if (priorityFilter !== 'all') filters.priority = priorityFilter;
      if (dateFilter !== 'all') {
        if (dateFilter === 'custom') {
          // Pass custom date range to backend using correct parameter names
          // Add time to ensure full day is included
          if (customDateRange.start) filters.date_from = customDateRange.start + ' 00:00:00';
          if (customDateRange.end) filters.date_to = customDateRange.end + ' 23:59:59';
        } else {
          filters.date_filter = dateFilter;
        }
      }
      if (typeFilter !== 'all') filters.ticket_type = typeFilter;
      if (globalFilter) filters.search = globalFilter;
       
      
      // Handle assigned filter
      if (assignedFilter === 'mine') {
        filters.assigned_filter = 'mine';
      } else if (assignedFilter === 'assigned') {
        filters.assigned_filter = 'assigned';
      } else if (assignedFilter === 'unassigned') {
        filters.assigned_filter = 'unassigned';
      } else if (assignedFilter === 'my_approvals') {
        filters.assigned_filter = 'my_approvals';
      } else if (assignedFilter === 'specific' && activeFilter.startsWith('user_')) {
        const userId = parseInt(activeFilter.replace('user_', ''));
        filters.assigned_to = userId;
      }
      
      const response = await getTickets(filters);
      if (response.status === 1 && response.data && response.data.tickets) {
        setTickets(response.data.tickets);
      } else {
        console.error('Failed to load tickets:', response.message);
        toast.error(response.message || 'Failed to load tickets');
        setTickets([]);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
      toast.error('Failed to load tickets');
      setTickets([]);
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleViewTicket = async (ticket) => {
    try {
      const response = await getTicketDetails(ticket.ticket_id);
      if (response.status === 1 && response.ticket) {
        setSelectedTicket({...ticket, ...response.ticket});
        setShowViewModal(true);
      } else {
        toast.error(response.message || 'Failed to load ticket details');
      }
    } catch (error) {
      console.error('Error loading ticket details:', error);
      toast.error('Failed to load ticket details');
    }
  };

  // New modern modal handlers with URL support
  const handleOpenTicketDetails = (ticketId) => {
    setSelectedTicketId(ticketId);
    setShowTicketDetails(true);
    
    // Update URL to include ticket parameter for deep linking
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('ticket', ticketId);
    navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    
    // When viewing ticket details, that's when we need to fetch the full contents
    fetchTicketWithFullContents(ticketId);
  };
  
  // Function to fetch a specific ticket with full content when needed
  const fetchTicketWithFullContents = async (ticketId) => {
    try {
      // Here we specifically request full details since we're showing the complete ticket
      const ticketDetails = await getTicketDetails(ticketId);
      
      // Update the selected ticket with full contents
      if (ticketDetails && ticketDetails.ticket) {
        setSelectedTicket(ticketDetails.ticket);
      }
    } catch (error) {
      console.error('Error fetching full ticket contents:', error);
      toast.error('Failed to load complete ticket details');
    }
  };

  const handleCloseTicketDetails = () => {
    setShowTicketDetails(false);
    setSelectedTicketId(null);
    
    // Remove ticket parameter from URL
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete('ticket');
    const newSearch = searchParams.toString();
    navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
  };

  const handleOpenNewTicket = () => {
    setShowNewTicket(true);
  };

  const handleCloseNewTicket = () => {
    setShowNewTicket(false);
  };

  const handleTicketCreated = (newTicketData) => {
 
    // Add the new ticket to the current list instead of reloading everything
    if (newTicketData && newTicketData.ticket) { 
      // Use the complete ticket data returned from the API
      setTickets(prevTickets => [newTicketData.ticket, ...prevTickets]);
    } else {
      // Fallback to reload if we don't have the complete ticket data 
      loadTickets();
    } 
  };

  const handleTicketUpdated = () => {
    loadTickets(); // Refresh the ticket list
  };

  const handleEditTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowEditModal(true);
  };

  const handleAssignTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowAssignModal(true);
  };

  const handleAssignSubmit = async (assigneeId) => {
    try {
      const response = await assignTicket(selectedTicket.ticket_id, assigneeId);
      if (response.status === 1) {
        toast.success('Ticket assigned successfully!');
        setShowAssignModal(false);
        setSelectedTicket(null);
        loadTickets();
      } else {
        toast.error(response.message || 'Failed to assign ticket');
      }
    } catch (error) {
      console.error('Error assigning ticket:', error);
      toast.error('Failed to assign ticket');
    }
  };

  const handleBulkAction = async (action) => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) {
      toast.warning('Please select tickets to perform bulk action');
      return;
    }

    const result = await Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} ${selectedRows.length} ticket(s)?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${action}!`
    });

    if (result.isConfirmed) {
      try {
        // Implement bulk action logic here
        toast.success(`${selectedRows.length} ticket(s) ${action}d successfully!`);
        setRowSelection({});
        loadTickets();
      } catch (error) {
        console.error(`Error performing bulk ${action}:`, error);
        toast.error(`Failed to ${action} tickets`);
      }
    }
  };

  // Unified flow: specialized submit handler removed

  const exportTickets = () => {
    // Export the filtered data from backend (already filtered)
    const dataToExport = filteredData;
    
    const csvContent = [
      ['Ticket Number', 'Subject', 'Creator', 'Status', 'Priority', 'Assignee', 'Created Date'],
      ...dataToExport.map(ticket => [
        ticket.ticket_number,
        ticket.title || ticket.ticket_subject,
        getOriginatorName(ticket),
        getStatusText(ticket.status || ticket.ticket_status, ticket),
        ticket.priority || ticket.ticket_priority || 'low',
        ticket.assigned_name || ticket.ticket_assigned_name || 'Unassigned',
        new Date(ticket.created_at || ticket.ticket_time * 1000).toLocaleDateString()
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Handler for closing all resolved tickets
  const handleCloseAllResolved = async () => {
    const resolvedTickets = tickets.filter(ticket => {
      const status = getStatusText(ticket.status || ticket.ticket_status, ticket);
      return status === 'resolved';
    });

    if (resolvedTickets.length === 0) {
      toast.info('No resolved tickets to close');
      return;
    }

    const result = await Swal.fire({
      title: 'Close All Resolved Tickets?',
      html: `This will close <strong>${resolvedTickets.length}</strong> resolved ticket${resolvedTickets.length > 1 ? 's' : ''}.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, close them!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setIsLoading(true);
      let successCount = 0;
      let errorCount = 0;

      for (const ticket of resolvedTickets) {
        try {
          const response = await closeTicket(ticket.ticket_id, 'Bulk closure of resolved tickets');
          if (response.status === 1) {
            successCount++;
          } else {
            errorCount++;
          }
        } catch (error) {
          errorCount++;
        }
      }

      setIsLoading(false);
      
      if (successCount > 0) {
        toast.success(`Successfully closed ${successCount} ticket${successCount > 1 ? 's' : ''}`);
        loadTickets(); // Reload to reflect changes
      }
      
      if (errorCount > 0) {
        toast.error(`Failed to close ${errorCount} ticket${errorCount > 1 ? 's' : ''}`);
      }
    }
  };

  // Handler for opening distribution modal
  const handleOpenDistributionModal = () => {  
    // Initialize with all IT users selected by default
    setSelectedITMembers(itUsers.map(u => parseInt(u.user_id || u.id)));
    setShowDistributionModal(true);
  };

  // Handler for toggling IT member selection
  const handleToggleITMember = (userId) => {
    setSelectedITMembers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // Handler for distributing closed unassigned tickets
  const handleDistributeTickets = async () => {
    if (selectedITMembers.length === 0) {
      toast.warning('Please select at least one IT member');
      return;
    }

    setIsDistributing(true);
    try {
      const ticketsPerMember = Math.floor(closedUnassignedTickets.length / selectedITMembers.length);
      const remainder = closedUnassignedTickets.length % selectedITMembers.length;
      
      let currentIndex = 0;
      const assignments = [];

      for (let i = 0; i < selectedITMembers.length; i++) {
        const memberId = selectedITMembers[i];
        const ticketsToAssign = ticketsPerMember + (i < remainder ? 1 : 0);
        
        for (let j = 0; j < ticketsToAssign; j++) {
          if (currentIndex < closedUnassignedTickets.length) {
            assignments.push({
              ticketId: closedUnassignedTickets[currentIndex].ticket_id,
              userId: memberId
            });
            currentIndex++;
          }
        }
      }

      // Perform assignments
      const results = await Promise.all(
        assignments.map(async ({ ticketId, userId }) => {
          try {
            const response = await assignTicket(ticketId, userId);
            return { success: response.status === 1, ticketId };
          } catch (error) {
            console.error(`Failed to assign ticket ${ticketId}:`, error);
            return { success: false, ticketId };
          }
        })
      );

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      if (successCount > 0) {
        toast.success(`Successfully distributed ${successCount} tickets across ${selectedITMembers.length} IT members`);
      }
      if (failCount > 0) {
        toast.warning(`Failed to assign ${failCount} tickets`);
      }

      setShowDistributionModal(false);
      loadTickets(); // Refresh the ticket list
    } catch (error) {
      console.error('Error distributing tickets:', error);
      toast.error('Failed to distribute tickets');
    } finally {
      setIsDistributing(false);
    }
  };

  // Render main component
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading tickets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Professional Header - aligned with Requisitions */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Subtitle, Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {isIT() ? 'IT Helpdesk' : 'Support Tickets'}
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  {isIT() ? 'Tickets & Conversations' : 'My Department Tickets'}
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0096DE' }}>
                    {(() => {
                      const filteredTotal = table.getFilteredRowModel().rows.length;
                      const showCount = Math.min(table.getState().pagination.pageSize, filteredTotal);
                      const isFiltered = (
                        statusFilter !== 'all' ||
                        priorityFilter !== 'all' ||
                        assignedFilter !== 'all' ||
                        dateFilter !== 'all' ||
                        !!globalFilter
                      );
                      return `${filteredTotal ? `Showing ${showCount} of ${filteredTotal} results` : '0 results'}${isFiltered ? ' (filtered)' : ''}`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: Controls */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {/* View Mode Toggle - Available to all users */}
                <div className="flex-shrink-0 inline-flex rounded-md shadow-sm border border-gray-300 bg-white" role="group">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-blue-50 text-blue-700 border-r border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 border-r border-gray-300'
                    }`}
                    title="Grid View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleViewModeChange('table')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Table View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>

                {/* Show advanced controls for IT users */}
                {isIT() && (
                  <>
                    {(statusFilter !== 'all' || priorityFilter !== 'all' || assignedFilter !== 'all' || dateFilter !== 'all' || typeFilter !== 'all' || globalFilter) && (
                      <button
                        onClick={() => {
                          setStatusFilter('all');
                          setPriorityFilter('all');
                          setAssignedFilter('all');
                          setDateFilter('all');
                          setTypeFilter('all');
                          setGlobalFilter('');
                          setActiveFilter('all');
                          setCustomDateRange({ start: '', end: '' });
                        }}
                        className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                      >
                        Clear Filters
                      </button>
                    )}

                    {/* Export Button */}
                    <button
                      onClick={exportTickets}
                      className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                      title="Export Tickets"
                    >
                      <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export
                    </button>

                    {/* Close All Resolved Tickets Button */}
                    {resolvedTicketsCount > 0 && (
                      <button
                        onClick={handleCloseAllResolved}
                        className="inline-flex items-center px-3 py-1.5 border border-green-300 rounded-md shadow-sm text-sm text-green-700 bg-green-50 hover:bg-green-100 focus:outline-none focus:ring-1 focus:ring-green-500 flex-shrink-0"
                        title="Close All Resolved Tickets"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Close Resolved ({resolvedTicketsCount})
                      </button>
                    )}

                    {/* Distribution Button - Show only if there are closed unassigned tickets */}
                    {closedUnassignedTickets.length > 0 && (
                      <button
                        onClick={handleOpenDistributionModal}
                        className="inline-flex items-center px-3 py-1.5 border border-orange-300 rounded-md shadow-sm text-sm text-orange-700 bg-orange-50 hover:bg-orange-100 focus:outline-none focus:ring-1 focus:ring-orange-500 flex-shrink-0"
                        title="Distribute Closed Unassigned Tickets"
                      >
                        <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        Distribute ({closedUnassignedTickets.length})
                      </button>
                    )}

                    {/* Refresh */}
                    <button
                      onClick={() => loadTickets()}
                      className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                      title="Refresh"
                      disabled={isLoading}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={isLoading ? '#0096DE' : 'currentColor'} style={{ color: isLoading ? '#0096DE' : 'currentColor' }}>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </button>

                    {/* View Toggle */}
                    <div className="flex bg-gray-100 rounded-lg p-0.5">
                      <button
                        onClick={() => setActiveView('dashboard')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          activeView === 'dashboard'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                        title="Dashboard"
                      >
                        <svg className="h-3 w-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        Dashboard
                      </button>
                      <button
                        onClick={() => setActiveView('tickets')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                          activeView === 'tickets'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-900'
                        }`}
                        title="Tickets"
                      >
                        <svg className="h-3 w-3 mr-1 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        Tickets
                      </button>
                    </div>

                    {/* Import Emails */}
                    <button
                      onClick={handleEmailImport}
                      disabled={isImporting}
                      className={`inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0 ${isImporting ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                    >
                      {isImporting ? (
                        <svg className="h-4 w-4 mr-1 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                        </svg>
                      )}
                      {isImporting ? 'Importing...' : 'Import Emails'}
                    </button>
                  </>
                )}

                {/* New Ticket - Available to all users */}
                <button
                  onClick={handleOpenNewTicket}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                  style={{ backgroundColor: '#0096DE', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: '#0096DE' }}
                >
                  <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  New Ticket
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* Analytics Dashboard - IT users only */}
      {activeView === 'dashboard' && isIT() && (
        <HelpdeskDashboard />
      )}

      {/* Main Content based on active view */}
      {activeView === 'tickets' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Enhanced Compact Toolbar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
            <div className="p-4">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
                {/* Search and Primary Filters */}
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 flex-1">
                  <div className="flex-1 max-w-sm">
                    <DebouncedInput
                      value={globalFilter ?? ''}
                      onChange={value => setGlobalFilter(String(value))}
                      placeholder="Search tickets, subjects, or users..."
                      className="w-full"
                    />
                  </div>
                  
                  {/* Filters for all users */}
                  <div className="flex space-x-2">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Status</option>
                      <option value="open">Open</option>
                      <option value="in_progress">Assigned</option>
                      <option value="escalated">Escalated</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                      
                      <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Priority</option>
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                        <option value="critical">Critical</option>
                      </select>
                      
                      <select
                        value={assignedFilter}
                        onChange={(e) => setAssignedFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Tickets</option>
                        <option value="mine">My Tickets</option>
                        <option value="assigned">Assigned</option>
                        <option value="unassigned">Unassigned</option>
                        {(user?.hod == 1 || user?.manager == 1 || user?.supervisor == 1 || user?.director == 1) && (
                          <option value="my_approvals">⏳ My Approvals</option>
                        )}
                      </select>
                      
                      <select
                        value={dateFilter}
                        onChange={(e) => {
                          const newValue = e.target.value;
                          setDateFilter(newValue);
                          if (newValue === 'custom') {
                            setShowCustomDateModal(true);
                          }
                        }}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="custom">Custom Range</option>
                      </select>
                      
                      {/* Custom Date Range Button - Shows when custom is selected */}
                      {dateFilter === 'custom' && (
                        <button
                          onClick={() => setShowCustomDateModal(true)}
                          className="px-3 py-2 text-sm border border-blue-300 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex items-center gap-2"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {customDateRange.start && customDateRange.end 
                            ? `${customDateRange.start} to ${customDateRange.end}` 
                            : 'Select Range'}
                        </button>
                      )}
                      
                      <select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="all">All Types</option>
                        <option value="support">General Support</option>
                        <option value="enhancement">Enhancement</option>
                        <option value="change_request">Change Request</option>
                        <option value="defect">Defect Report</option>
                        <option value="new_user">New User Setup</option>
                        <option value="staff_movement">Staff Movement</option>
                        <option value="access_modification">Edams Access Modification</option>
                        <option value="access_termination">Access Termination</option>
                        <option value="password_reset">Password Reset</option>
                      </select>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  {/* Clear Filters - All users */}
                  {(statusFilter !== 'all' || priorityFilter !== 'all' || assignedFilter !== 'all' || dateFilter !== 'all' || typeFilter !== 'all' || globalFilter) && (
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setPriorityFilter('all');
                        setAssignedFilter('all');
                        setDateFilter('all');
                        setTypeFilter('all');
                        setGlobalFilter('');
                        setActiveFilter('all');
                        setCustomDateRange({ start: '', end: '' });
                      }}
                      className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filter Summary - IT users only */}
              {isIT() && (statusFilter !== 'all' || priorityFilter !== 'all' || assignedFilter !== 'all' || dateFilter !== 'all' || typeFilter !== 'all' || globalFilter) && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-gray-500">Active filters:</span>
                    {globalFilter && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                        Search: "{globalFilter}"
                      </span>
                    )}
                    {statusFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-800">
                        Status: {getFilterLabel('status', statusFilter)}
                      </span>
                    )}
                    {priorityFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
                        Priority: {getFilterLabel('priority', priorityFilter)}
                      </span>
                    )}
                    {assignedFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-800">
                        Assignment: {getFilterLabel('assigned', assignedFilter)}
                      </span>
                    )}
                    {typeFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-teal-100 text-teal-800">
                        Type: {typeFilter.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    )}
                    {dateFilter !== 'all' && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-pink-100 text-pink-800">
                        Date: {dateFilter === 'custom' 
                          ? `${customDateRange.start || 'Start'} to ${customDateRange.end || 'End'}` 
                          : getFilterLabel('date', dateFilter)}
                      </span>
                    )}
                    <span className="text-gray-500 ml-2">
                      ({filteredData.length} of {tickets.length} tickets)
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Table or Grid View */}
          {viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {(() => {
                const rows = table.getRowModel().rows;
                return rows.length === 0 ? (
                  <div className="col-span-full bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                    <div className="flex flex-col items-center">
                      <svg className="h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                      <p className="text-gray-500 mb-4">Get started by creating a new ticket or adjusting your filters.</p>
                      <button
                        onClick={handleOpenNewTicket}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create New Ticket
                      </button>
                    </div>
                  </div>
                ) : (
                  rows.map(row => {
                    const ticket = row.original;
                    
                    // Get subject - handle both formats
                    const subject = ticket.title || ticket.ticket_subject || 'No Subject';
                    
                    // Get ticket type
                    const displayType = (ticket.ticket_number?.includes("SUP") && ticket.ticket_type === "enhancement") 
                      ? "Change Request" 
                      : ticket.ticket_type?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase()) || "Support";
                    
                    const typeColors = {
                      'Support': 'bg-blue-50 text-blue-700 border-blue-200',
                      'Enhancement': 'bg-purple-50 text-purple-700 border-purple-200',
                      'Change Request': 'bg-orange-50 text-orange-700 border-orange-200',
                      'Defect': 'bg-red-50 text-red-700 border-red-200' 
                    };
                    const typeColor = typeColors[displayType] || 'bg-gray-50 text-gray-700 border-gray-200';
                    
                    // Get priority - handle both formats
                    const priorityValue = ticket.priority || ticket.ticket_priority;
                    let priorityText = "Low";
                    let priorityKey = "low";
                    if (priorityValue === "medium" || priorityValue == 2) {
                      priorityText = "Med";
                      priorityKey = "medium";
                    } else if (priorityValue === "high" || priorityValue == 3) {
                      priorityText = "High";
                      priorityKey = "high";
                    } else if (priorityValue === "urgent" || priorityValue == 4) {
                      priorityText = "Urgent";
                      priorityKey = "urgent";
                    } else if (priorityValue === "critical" || priorityValue == 5) {
                      priorityText = "Critical";
                      priorityKey = "critical";
                    }
                    
                    // Priority styling with dots
                    const priorityColors = {
                      'low': { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', dot: 'bg-gray-400' },
                      'medium': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200', dot: 'bg-yellow-500' },
                      'high': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', dot: 'bg-orange-500' },
                      'urgent': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
                      'critical': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', dot: 'bg-red-600' }
                    };
                    const priorityColor = priorityColors[priorityKey] || priorityColors['low'];
                    
                    // Get status - use getStatusText helper
                    const statusValue = ticket.status || ticket.ticket_status;
                    const statusText = getStatusText(statusValue, ticket);
                    
                    // Status styling
                    const statusColors = {
                      'Open': 'bg-blue-100 text-blue-800',
                      'Assigned': 'bg-purple-100 text-purple-800',
                      'In Progress': 'bg-purple-100 text-purple-800',
                      'Escalated': 'bg-orange-100 text-orange-800',
                      'Resolved': 'bg-green-100 text-green-800',
                      'Closed': 'bg-gray-100 text-gray-800'
                    };
                    const statusColor = statusColors[statusText] || 'bg-gray-100 text-gray-800';
                    
                    // Get ticket ID/number
                    const ticketId = ticket.ticket_number || ticket.ticket_id;
                    
                    // Get user name
                    const userName = getOriginatorName(ticket);
                    
                    // Get assigned name
                    const assignedName = ticket.assigned_name || ticket.ticket_assigned_name;
                    
                    // Get date
                    const dateValue = ticket.created_at || ticket.ticket_time;
                    let dateDisplay = '';
                    let timeDisplay = '';
                    try {
                      let date;
                      if (typeof dateValue === 'string') {
                        date = new Date(dateValue);
                      } else if (dateValue) {
                        date = new Date(dateValue * 1000);
                      }
                      if (date && !isNaN(date.getTime())) {
                        dateDisplay = date.toLocaleDateString('en-CA');
                        timeDisplay = date.toLocaleTimeString('en-CA', {hour: '2-digit', minute: '2-digit', hour12: false});
                      }
                    } catch (e) {
                      console.error('Date parsing error:', e);
                      dateDisplay = 'Invalid Date';
                    }
                    
                    return (
                      <div 
                        key={ticket.ticket_id} 
                        className="bg-white rounded-lg shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 group flex flex-col h-full cursor-pointer overflow-hidden"
                        onClick={() => handleOpenTicketDetails(ticket.ticket_id)}
                      >
                        {/* Header with Ticket ID */}
                        <div className="px-4 pt-3 pb-2 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-bold text-blue-600">
                              {ticketId}
                            </span>
                            <span className={`text-xs font-medium px-2 py-0.5 rounded ${statusColor}`}>
                              {statusText}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${typeColor}`}>
                              {displayType}
                            </span>
                            <div className="flex items-center space-x-1">
                              <div className={`w-1.5 h-1.5 rounded-full ${priorityColor.dot}`}></div>
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium border ${priorityColor.bg} ${priorityColor.text} ${priorityColor.border}`}>
                                {priorityText}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="px-4 py-3 flex-grow">
                          {/* Subject */}
                          <h3 className="font-semibold text-sm text-gray-900 mb-2 line-clamp-2 leading-snug">
                            {subject}
                          </h3>

                          {/* Details snippet */}
                          {(ticket.details || ticket.ticket_details) ? (
                            <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                              {ticket.details || ticket.ticket_details}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 italic line-clamp-2 leading-relaxed">
                              {subject.length > 80 ? subject : `${subject}...`}
                            </p>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 space-y-1.5">
                          {/* User - More prominent */}
                          <div className="flex items-center text-sm text-gray-700">
                            <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="truncate font-semibold">{userName}</span>
                          </div>
                          
                          {/* Assigned To (if assigned) */}
                          {assignedName && assignedName !== 'Unassigned' && (
                            <div className="flex items-center text-xs text-gray-600">
                              <svg className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="truncate">Assigned: {assignedName}</span>
                            </div>
                          )}
                          
                          {/* Date */}
                          <div className="flex items-center text-xs text-gray-500">
                            <svg className="w-3.5 h-3.5 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>{dateDisplay}</span>
                            {timeDisplay && <span className="ml-1">• {timeDisplay}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })
                );
              })()}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                            onClick={header.column.getCanSort() ? header.column.getToggleSortingHandler() : undefined}
                          >
                            <div className="flex items-center space-x-1">
                              {flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getCanSort() && (
                                <span className="ml-2">
                                  {header.column.getIsSorted() === 'desc' ? '↓' : header.column.getIsSorted() === 'asc' ? '↑' : '↕'}
                                </span>
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(() => {
                      const rows = table.getRowModel().rows; 
                      return rows.length === 0 ? (
                        <tr>
                          <td colSpan={columns.length} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <svg className="h-12 w-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                              </svg>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                              <p className="text-gray-500 mb-4">Get started by creating a new ticket or adjusting your filters.</p>
                              <button
                                onClick={handleOpenNewTicket}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Create New Ticket
                              </button>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        table.getRowModel().rows.map(row => (
                          <tr 
                            key={row.id} 
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleOpenTicketDetails(row.original.ticket_id)}
                          >
                            {row.getVisibleCells().map(cell => (
                              <td key={cell.id} className="px-6 py-4 whitespace-nowrap">
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            ))}
                          </tr>
                        ))
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Enhanced Pagination with Page Size Controls - Always show if there are any tickets */}
          {filteredData.length > 0 && (
            <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-b-lg">
              <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div className="flex items-center space-x-4">
                    <p className="text-sm text-gray-700">
                      Showing{' '}
                      <span className="font-medium">
                        {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium">
                        {Math.min(
                          (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                          table.getFilteredRowModel().rows.length
                        )}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium">{table.getFilteredRowModel().rows.length}</span> results
                    </p>
                     
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {/* Page Size Selector in Pagination */}
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">Rows per page:</span>
                      <select
                        value={table.getState().pagination.pageSize}
                        onChange={e => {
                          table.setPageSize(Number(e.target.value))
                        }}
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {[10, 25, 50, 100, 200, 500].map(pageSize => (
                          <option key={pageSize} value={pageSize}>
                            {pageSize}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">First</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      
                      {/* Page Numbers */}
                      {(() => {
                        const currentPage = table.getState().pagination.pageIndex;
                        const totalPages = table.getPageCount();
                        const pages = [];
                        
                        // Always show first page
                        if (totalPages > 0) {
                          pages.push(0);
                        }
                        
                        // Show pages around current page
                        for (let i = Math.max(1, currentPage - 1); i <= Math.min(totalPages - 2, currentPage + 1); i++) {
                          if (i > 0 && i < totalPages - 1) {
                            pages.push(i);
                          }
                        }
                        
                        // Always show last page if there's more than 1 page
                        if (totalPages > 1) {
                          pages.push(totalPages - 1);
                        }
                        
                        // Remove duplicates and sort
                        const uniquePages = [...new Set(pages)].sort((a, b) => a - b);
                        
                        return uniquePages.map((pageIndex, i) => (
                          <button
                            key={pageIndex}
                            onClick={() => table.setPageIndex(pageIndex)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              pageIndex === currentPage
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {pageIndex + 1}
                          </button>
                        ));
                      })()}
                      
                      <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                        className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <button
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Last</span>
                        <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 011.414-1.414l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
        </div>
      )}

      {/* Chat View */}
      {activeView === 'chat' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-96">
            <div className="p-6 text-center text-gray-500">
              Live Chat functionality coming soon...
            </div>
          </div>
        </div>
      )}

      {/* Quick Create Ticket Types Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Create New Ticket</h3>
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ticketTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setShowCreateForm(false);
                      setShowNewTicket(true);
                      // seed NewTicketModal with chosen type via simple event/state bridge
                      setTimeout(() => {
                        const event = new CustomEvent('helpdesk:setType', { detail: { type: type.id } });
                        window.dispatchEvent(event);
                      }, 0);
                    }}
                    className={`p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-gray-400 transition-colors text-left ${type.bgColor} ${type.borderColor}`}
                  >
                    <div className="flex items-center mb-2">
                      <span className="text-2xl mr-3">{type.icon}</span>
                      <h4 className="font-medium text-gray-900">{type.title}</h4>
                    </div>
                    <p className="text-sm text-gray-600">{type.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Specialized Ticket Modals removed - unified flow via NewTicketModal */}

      {/* Ticket View Modal */}
      {showViewModal && selectedTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Ticket #{selectedTicket.ticket_number}
              </h3>
              <button
                onClick={() => setShowViewModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Subject</h4>
                <p className="text-gray-700">{selectedTicket.ticket_subject}</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Details</h4>
                <div className="text-gray-700 prose max-w-none">
                  {(() => {
                    const content = selectedTicket.ticket_details || '';
                    if (content.includes('<')) {
                      return (
                        <div dangerouslySetInnerHTML={{
                          __html: content
                            .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '')
                            .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
                            .replace(/<link[^>]*>/gi, '')
                            .replace(/<form[^>]*>[\s\S]*?<\/form>/gi, '')
                            .replace(/<img([^>]*)>/gi, '<img$1 class="img-fluid rounded max-w-full h-auto" />')
                            .replace(/\n/g, '<br>')
                        }} />
                      );
                    }
                    return <div className="whitespace-pre-line">{content}</div>;
                  })()}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Status</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[getStatusText(selectedTicket.ticket_status)]}`}>
                    {getStatusText(selectedTicket.ticket_status)}
                  </span>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Priority</h4>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[selectedTicket.ticket_priority || 'medium']}`}>
                    {formatPriority(selectedTicket.ticket_priority) || 'MEDIUM'}
                  </span>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Created</h4>
                  <p className="text-gray-700 text-sm">
                    {new Date(selectedTicket.ticket_time * 1000).toLocaleString()}
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">Assignee</h4>
                  <p className="text-gray-700 text-sm">
                    {selectedTicket.ticket_assigned_name || 'Unassigned'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedTicket && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Assign Ticket</h3>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Assign ticket #{selectedTicket.ticket_number} to:
                </p>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {itUsers.map((user) => (
                    <button
                      key={user.user_id || user.id}
                      onClick={() => handleAssignSubmit(user.user_id || user.id)}
                      className="w-full text-left p-3 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-gray-600">
                            {(user.name || user.user_full_name || '').split(' ').map(n => n[0]).join('')}
                          </span>
                        </div>
                        <span className="font-medium">{user.name || user.user_full_name}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modern Ticket Details Modal */}
      <TicketDetailsModal 
        isOpen={showTicketDetails}
        onClose={handleCloseTicketDetails}
        ticketId={selectedTicketId}
        onTicketUpdate={handleTicketUpdated}
        currentUser={user}
      />

      {/* Modern New Ticket Modal */}
      <NewTicketModal 
        isOpen={showNewTicket}
        onClose={handleCloseNewTicket}
        onTicketCreated={handleTicketCreated}
      />

      {/* Ticket Distribution Modal */}
      {showDistributionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-lg bg-white">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-gray-900">
                Distribute Closed Unassigned Tickets
              </h3>
              <button
                onClick={() => setShowDistributionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={isDistributing}
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-blue-900 mb-1">Distribution Summary</h4>
                    <p className="text-sm text-blue-700">
                      <strong>{closedUnassignedTickets.length}</strong> closed tickets will be distributed equally across{' '}
                      <strong>{selectedITMembers.length}</strong> selected IT member(s)
                    </p>
                    {selectedITMembers.length > 0 && (
                      <p className="text-sm text-blue-600 mt-2">
                        Each member will receive approximately{' '}
                        <strong>{Math.ceil(closedUnassignedTickets.length / selectedITMembers.length)}</strong> ticket(s)
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Select IT Members ({selectedITMembers.length}/{itUsers.length})
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedITMembers(itUsers.map(u => parseInt(u.user_id || u.id)))}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setSelectedITMembers([])}
                      className="text-xs text-gray-600 hover:text-gray-700 font-medium"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto">
                  {itUsers.length === 0 ? (
                    <div className="p-8 text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No IT Members Found</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        Please make sure IT users are configured in the system.
                      </p>
                    </div>
                  ) : (
                    itUsers.map((user) => {
                    const userId = parseInt(user.user_id || user.id);
                    const isSelected = selectedITMembers.includes(userId);
                    const ticketsForThisMember = selectedITMembers.length > 0 
                      ? Math.floor(closedUnassignedTickets.length / selectedITMembers.length) + 
                        (selectedITMembers.indexOf(userId) < closedUnassignedTickets.length % selectedITMembers.length ? 1 : 0)
                      : 0;

                    return (
                      <div
                        key={userId}
                        onClick={() => handleToggleITMember(userId)}
                        className={`flex items-center justify-between p-3 border-b border-gray-100 last:border-b-0 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center flex-1">
                          <div className={`h-5 w-5 rounded border-2 mr-3 flex items-center justify-center transition-colors ${
                            isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                          }`}>
                            {isSelected && (
                              <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                          <div className="flex items-center">
                            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                              <span className="text-xs font-medium text-gray-600">
                                {(user.name || user.user_full_name || '').split(' ').map(n => n[0]).join('')}
                              </span>
                            </div>
                            <span className={`font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>
                              {user.name || user.user_full_name}
                            </span>
                          </div>
                        </div>
                        {isSelected && ticketsForThisMember > 0 && (
                          <div className="ml-3">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {ticketsForThisMember} ticket{ticketsForThisMember !== 1 ? 's' : ''}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDistributionModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                disabled={isDistributing}
              >
                Cancel
              </button>
              <button
                onClick={handleDistributeTickets}
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                disabled={isDistributing || selectedITMembers.length === 0}
              >
                {isDistributing ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Distributing...
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    Distribute Tickets
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Date Range Modal */}
      {showCustomDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowCustomDateModal(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Select Date Range</h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <CustomDatePicker
                  value={customDateRange.start ? new Date(customDateRange.start).toLocaleDateString('en-GB') : ''}
                  onChange={(date) => {
                    if (date) {
                      const parts = date.split('/');
                      const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                      setCustomDateRange(prev => ({ ...prev, start: formatted }));
                    } else {
                      setCustomDateRange(prev => ({ ...prev, start: '' }));
                    }
                  }}
                  placeholder="Select start date"
                  placement="left"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <CustomDatePicker
                  value={customDateRange.end ? new Date(customDateRange.end).toLocaleDateString('en-GB') : ''}
                  onChange={(date) => {
                    if (date) {
                      const parts = date.split('/');
                      const formatted = `${parts[2]}-${parts[1]}-${parts[0]}`;
                      setCustomDateRange(prev => ({ ...prev, end: formatted }));
                    } else {
                      setCustomDateRange(prev => ({ ...prev, end: '' }));
                    }
                  }}
                  placeholder="Select end date"
                  placement="left"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setCustomDateRange({ start: '', end: '' });
                  setDateFilter('all');
                  setShowCustomDateModal(false);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
              <button
                onClick={() => setShowCustomDateModal(false)}
                disabled={!customDateRange.start || !customDateRange.end}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HelpTickets;